import React from "react";
import {useAtom} from "jotai";
import {Box, IconButton, Modal, Stack, Typography} from "@mui/material";
import {keyframes} from "@emotion/react";
import {HugeiconsIcon} from "@hugeicons/react";
import {PhoneCallIcon, PhoneOff01Icon} from "@hugeicons/core-free-icons";
import {CallConfirmDialogAtom} from "../../pages/project/atoms";

const ringPulse = keyframes`
  0% { transform: scale(0.85); opacity: 0.55; }
  70% { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(92vw, 320px)",
  bgcolor: "background.paper",
  borderRadius: "28px",
  border: "0px solid #000",
  boxShadow: 24,
  px: 3,
  py: 4,
  outline: "none",
};

const ringSx = (delay) => ({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  border: (theme) => `2px solid ${theme.palette.success.main}`,
  animation: `${ringPulse} 2s ease-out infinite`,
  animationDelay: `${delay}s`,
});

// Phone-call-style confirm: green answer button, red hang-up button, avatar with pulsing rings —
// pairs with useCallConfirm. Kept as its own atom/component so it never collides with the
// generic yes/no ConfirmDialogModal used elsewhere.
export const CallConfirmDialogModal = () => {
  const [dialog, setDialog] = useAtom(CallConfirmDialogAtom);

  const handleClose = () => {
    setDialog((prev) => ({...prev, open: false}));
  };

  const handleConfirm = () => {
    dialog.resolve(true);
    handleClose();
  };

  const handleCancel = () => {
    dialog.resolve(false);
    handleClose();
  };

  return (
    <Modal open={dialog.open} onClose={handleCancel}>
      <Stack alignItems="center" gap={0.5} sx={style}>
        <Box sx={{position: "relative", width: 84, height: 84, display: "grid", placeItems: "center", mb: 2}}>
          <Box aria-hidden sx={ringSx(0)} />
          <Box aria-hidden sx={ringSx(0.6)} />
          <Box
            sx={{
              position: "relative",
              width: 64,
              height: 64,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: (theme) => theme.palette.success.light || theme.palette.success.main,
              color: "#fff",
            }}
          >
            <HugeiconsIcon icon={PhoneCallIcon} size={30} className="phone-shake" />
          </Box>
        </Box>

        <Typography align="center" fontWeight={700} fontSize="1.05rem" sx={{px: 1}}>
          {dialog.title}
        </Typography>
        {dialog.subtitle && (
          <Typography align="center" fontSize="0.85rem" color="text.secondary" sx={{px: 1, mb: 1}}>
            {dialog.subtitle}
          </Typography>
        )}

        <Stack direction="row" justifyContent="center" alignItems="flex-start" gap={6} sx={{mt: 3}}>
          <Stack alignItems="center" gap={0.75}>
            <IconButton
              onClick={handleCancel}
              sx={{
                width: 60,
                height: 60,
                bgcolor: (theme) => theme.palette.error.main,
                color: "#fff",
                "&:hover": {bgcolor: (theme) => theme.palette.error.dark},
              }}
            >
              <HugeiconsIcon icon={PhoneOff01Icon} size={26} />
            </IconButton>
            <Typography fontSize="0.75rem" color="text.secondary">Decline</Typography>
          </Stack>

          <Stack alignItems="center" gap={0.75}>
            <IconButton
              onClick={handleConfirm}
              sx={{
                width: 60,
                height: 60,
                bgcolor: (theme) => theme.palette.success.main,
                color: "#fff",
                "&:hover": {bgcolor: (theme) => theme.palette.success.dark},
              }}
            >
              <HugeiconsIcon icon={PhoneCallIcon} size={26} />
            </IconButton>
            <Typography fontSize="0.75rem" color="text.secondary">Call</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
